import { inject, Injectable } from '@angular/core';
import { QuestionModel } from '../question/question.model';
import { UtilsService } from './utils.service';
import { cloneDeep, isEmpty, omit } from 'lodash-es';
import { FormElementsEnum } from '../form/form-elements.enum';
import { ControlModel } from '../question/control.model';
import { RulesType } from '../question/rules-list/rules-type.enum';
import { RuleModel } from '../rule/rule.model';
import { FormModel } from '../form/form.model';
import { diff, IChange, Operation } from 'json-diff-ts';
import { IIntegrationQuestionGroup } from '../shared/models/integration/integration-question-group.interface';
import { IIntegrationQuestion } from '../shared/models/integration/integration-question.interface';
import { IIntegrationRule } from '../shared/models/integration/integration-rule.interface';
import { v4 as uuidv4 } from 'uuid';
import { RuleLogicEnum } from '../rule/rule-logic.enum';
import {
  DROPDOWN_SELECTION_COMPONENTS,
  NUMBER_SELECTION_COMPONENTS,
} from '../question/editors/phar-rules-editors/selection-types';
@Injectable({
  providedIn: 'root',
})
export class IntegrationService {
  DEFAULT_LANGUAGE = {
    language_id: 1,
    language_code: 'en-EN',
  };
  logicsMap: Record<string, any[]> = {};
  utilsService: UtilsService = inject(UtilsService);
  private readonly controlTypeTranslationMap = {
    [FormElementsEnum.VisualAnalogScale]: this.translateVASControls.bind(this),
    [FormElementsEnum.NumericRatingScale]: this.translateNRSControls.bind(this),
    [FormElementsEnum.MultiSelect]: this.translateBooleanControls.bind(this), // multiselect labels are processing as Boolean labels
    [FormElementsEnum.MultiSelectQuantity]: this.translateBooleanControls.bind(this),
    [FormElementsEnum.VerbalRatingScale]: this.translateVRSControls.bind(this),
    [FormElementsEnum.SingleSelect]: this.translateVRSControls.bind(this), // label processing is the same as VRS
    [FormElementsEnum.Boolean]: this.translateBooleanControls.bind(this),
    [FormElementsEnum.TextBlock]: this.translateTextControls.bind(this),
    [FormElementsEnum.TextInput]: this.translateTextControls.bind(this),
    [FormElementsEnum.Number]: this.translateNumberControls.bind(this),
    [FormElementsEnum.DatePicker]: this.translateTextControls.bind(this),
    [FormElementsEnum.TimePicker]: this.translateTextControls.bind(this),
    [FormElementsEnum.Image]: this.translateTextControls.bind(this),
  };

  transformQuestion(
    question: QuestionModel,
    form: FormModel,
    previousForm?: FormModel,
  ): {
    integrationJSON: IIntegrationQuestionGroup[];
    integrationJSONChanges?: Partial<IIntegrationQuestionGroup>[];
  } {
    question.controls = question.controls.filter(c => c.controlType !== 'dropzone');
    const newIntegration = this.generateIntegration(question, form, false) as IIntegrationQuestionGroup[];
    // Reset the logic map to prevent state conflicts with previous operations
    this.logicsMap = {};

    if (!previousForm) {
      // return { ...question, integration: newIntegration };
      return { integrationJSON: newIntegration };
    }

    const currentQuestionInPrevVersion = this.findQuestionInPreviousForm(question.questionGuid, previousForm);

    if (!currentQuestionInPrevVersion) {
      return { integrationJSON: newIntegration };
    }

    const previousIntegration = this.generateIntegration(
      currentQuestionInPrevVersion,
      previousForm,
    ) as IIntegrationQuestionGroup[];
    const newIntegrationOnlyChanges = this.generateIntegration(question, form, true);

    const integrationChanges = this.compareQuestionGroups(
      previousIntegration,
      newIntegrationOnlyChanges as IIntegrationQuestionGroup[],
    );

    // If there are changes, return only the changes, otherwise return empty integration
    // const finalIntegration = integrationChanges ? { changes: integrationChanges, in} : [];

    return {
      integrationJSON: newIntegration,
      integrationJSONChanges: integrationChanges ? [integrationChanges] : [],
    };
  }

  compareQuestionGroups(oldIntegration: IIntegrationQuestionGroup[], newIntegration: IIntegrationQuestionGroup[]) {
    const [oldIntegrationQg] = oldIntegration;
    const [newIntegrationQg] = newIntegration;
    if (oldIntegrationQg.is_deleted) {
      return null;
    }
    if (oldIntegrationQg.uuid !== newIntegrationQg.uuid) {
      // if the uuids no sense to compare so return null;
      return null;
    }
    const diffs = diff(omit(oldIntegrationQg, 'questions'), omit(newIntegrationQg, 'questions'));
    let updatedQg = diffs.length
      ? {
        uuid: oldIntegrationQg.uuid,
        ...this.translateChanges(diffs, newIntegrationQg),
      }
      : {};

    if ('is_deleted' in updatedQg && !!updatedQg.is_deleted) {
      // if the question group is deleted no sense of compare so return updated with is_delete
      return updatedQg;
    }

    const questionsChanges = this.compareQuestions(oldIntegrationQg.questions, newIntegrationQg.questions);

    if (isEmpty(questionsChanges)) {
      return isEmpty(updatedQg) ? null : updatedQg;
    }

    const updatedQuestions = Object.values(questionsChanges);
    /*const containsIsDelete = updatedQuestions.some((item: { is_deleted?: boolean }) => item.is_deleted);
    if (containsIsDelete) {
      // @TODO temporary solution, when LightIt fix the order then this should be deleted
      updatedQuestions = updatedQuestions.map((item: { order?: number }) => omit(item, 'order'));
    }*/

    if (updatedQuestions && updatedQuestions.length) {
      updatedQg = {
        ...updatedQg,
        questions: updatedQuestions,
      };
    }

    updatedQg = {
      uuid: oldIntegrationQg.uuid, //just in case to ensure that the uuid is always there
      ...updatedQg,
    };
    // if only one key is there it means that no updates at all
    return Object.keys(updatedQg).length === 1 ? null : updatedQg;
  }

  compareQuestions(oldQuestions: IIntegrationQuestion[], newQuestions: IIntegrationQuestion[]) {
    // update
    // add
    // delete
    const updates = {};
    const checkedQ = [];

    oldQuestions.forEach(oldQ => {
      const newQ = newQuestions.find(nQ => nQ.uuid === oldQ.uuid);
      checkedQ.push(newQ.uuid);
      const diffs = diff(oldQ, newQ);
      if (diffs.length) {
        if (!(newQ.uuid in updates)) {
          updates[newQ.uuid] = {
            uuid: oldQ.uuid,
          };
        }
        updates[newQ.uuid] = {
          ...updates[newQ.uuid],
          ...this.translateChanges(diffs, newQ),
        };
      }
    });

    if (checkedQ.length < newQuestions.length) {
      //there is some questions/controls that are NEW or not handled;
      newQuestions
        .filter(newQ => !checkedQ.includes(newQ.uuid))
        .forEach(newQ => {
          updates[newQ.uuid] = {
            ...newQ,
          };
        });
    }

    return updates;
  }

  translateChanges(changes: IChange[], entity?: any) {
    let translation = {};

    changes.forEach((change: IChange) => {
      if (change.type !== Operation.REMOVE) {
        if (entity && (change.embeddedKey || change.changes)) {
          //its array or its whole object containing changes
          translation = { ...translation, [change.key]: entity[change.key] };
        } else {
          translation = { ...translation, [change.key]: change.value };
        }
      }
    });
    return translation;
  }

  /**
   * Finds a question in the previous form version by its GUID
   * @param questionGuid - The GUID of the question to find
   * @param previousForm - The previous version of the form
   * @returns The found question or undefined
   */
  private findQuestionInPreviousForm(questionGuid: string, previousForm: FormModel): QuestionModel | undefined {
    const prevQuestions = Object.values(previousForm?.questionsPopulated || {});
    return prevQuestions.find((q: QuestionModel) => q.questionGuid === questionGuid) as QuestionModel;
  }

  private generateIntegration(question: QuestionModel, form: FormModel, detectOnlyChanges?: boolean) {
    let order = 1;
    // const page = form.body.pages[question.pageIndex];
    const page = form.body.pages.find(p => p.page_uuid === question.questionPageUuid);
    const questionInPage = page?.questions.find((q: { id: number }) => q.id === question.id);
    if (questionInPage) {
      order = (questionInPage?.order || 0) + 1; // our order is 0 based, but the other system is 1 based
    }
    //early return if the question group is deleted
    if (question.isDeleted && detectOnlyChanges) {
      return [
        {
          uuid: question.questionGroupGuid,
          is_deleted: true,
        },
      ];
    }
    return [
      //it should be an array of objects
      {
        uuid: question.questionGroupGuid || this.utilsService.generateUUID(),
        code: question.integration[0]?.code || question.title,
        order,
        repeat_key: 'repeat key name',
        repeats: question.allowedIterations || 1,
        is_displayed: false, // this is not used by LightIt, but its required
        is_deleted: question.isDeleted,
        translations: [
          {
            name: question.label,
            units: '',
            ...this.getLanguage(form),
          },
        ],
        questions: this.translateControls(question, form),
      },
    ];
  }

  private translateControls(question: QuestionModel, form: FormModel) {
    const controls = cloneDeep(question.controls);
    let translatedControls = [];
    controls.forEach((ctrl: ControlModel) => {
      if (this.controlTypeTranslationMap[ctrl.controlType]) {
        const translationObject = this.controlTypeTranslationMap[ctrl.controlType](ctrl, form);
        this.generateLogics(ctrl, controls, form.formShort.formGuid);
        translatedControls.push(translationObject);
      }
    });
    // append logic to the controls
    // child should append to itself the logic that is generated by the parent
    translatedControls = translatedControls.map((ctrl: { uuid: string; logics: any[] }) => {
      return { ...ctrl, logics: this.logicsMap[ctrl.uuid] || [] };
    });

    return translatedControls;
  }

  private generateLogics(control: ControlModel, controls: ControlModel[], formGuid: string): void {
    if (!control.dependencies[RulesType.Conditions]) {
      return;
    }
    this.translateDependencies(control, 'hide', controls, formGuid);
    this.translateDependencies(control, 'show', controls, formGuid);
  }

  private translateDependencies(
    control: ControlModel,
    action: 'hide' | 'show',
    controls: ControlModel[],
    formGuid: string,
  ) {
    control.dependencies[RulesType.Conditions][action].data.forEach((rule: RuleModel) => {
      const targetControl = controls.find(ctrl => ctrl.controlID === rule.triggeringControlId);
      if (!targetControl) {
        return;
      }
      // count the logics to the control so we need to give proper incremental name
      const name = `REF_${(this.logicsMap[targetControl.question_uuid] || []).length + 1}`;
      const value = (rule.value + 1).toString(); // this is the actual value to compare
      let COMPARISON_OBJECT = {};
      if (control.controlType === FormElementsEnum.MultiSelect) {
        // for now only MultiSelect has different comparison operator than the others
        COMPARISON_OBJECT = {
          in: [
            value, // when Multiselect, the order of the value is important, it should be at the beginning of the array, else in the end
            {
              var: name, /// the REF_XX
            },
          ],
        };
      } else {
        COMPARISON_OBJECT = {
          '==': [
            {
              var: name, /// the REF_XX
            },
            value,
          ],
        };
      }

      const logic = {
        question_uuid: control.question_uuid, //uuid of the parent
        condition: {
          if: [COMPARISON_OBJECT, true, false],
        },
        action,
        name,
        references: {
          questionnaire_uuid: formGuid,
          page_uuid: targetControl.page_uuid,
          question_uuid: control.question_uuid,
        },
      };

      this.logicsMap[targetControl.question_uuid] = [...(this.logicsMap[targetControl.question_uuid] || []), logic];
    });
  }

  public translateLogic(
    control: ControlModel,
    question: QuestionModel,
    targetControl: ControlModel,
    targetQuestion: QuestionModel,
    rule: IIntegrationRule,
    form: FormModel,
  ) {
    // trimmed because of some limitation in the other side
    const name = uuidv4().slice(0, 10);
    const varRef = { var: name };
    const logic = (rule.logic ?? RuleLogicEnum.Equal) as RuleLogicEnum;

    const primaryValue = this.processRuleValue(rule.controlResponseValueIndex, control.controlType);
    let COMPARISON_OBJECT = this.buildComparisonObject(varRef, primaryValue, logic, control.controlType);

    if (rule.condition && rule.additionalValue !== null && rule.additionalValue !== '') {
      const additionalValue = this.processRuleValue(rule.additionalValue, control.controlType);
      const additionalComparison = this.buildComparisonObject(varRef, additionalValue, logic, control.controlType);
      COMPARISON_OBJECT = { [rule.condition]: [COMPARISON_OBJECT, additionalComparison] };
    }

    return {
      question_uuid: control.question_uuid, //uuid of the parent
      condition: {
        if: [COMPARISON_OBJECT, true, false],
      },
      action: rule.action,
      name,
      references: {
        questionnaire_uuid: form.formShort.formGuid,
        page_uuid: targetControl.page_uuid,
        question_uuid: control.question_uuid,
      },
    };
  }

  private processRuleValue(rawValue: any, controlType: string): any {
    if (DROPDOWN_SELECTION_COMPONENTS.includes(controlType as FormElementsEnum)) {
      // Dropdown controls store 0-based index; backend/runtime expects 1-based string
      return (rawValue + 1).toString();
    }
    if (NUMBER_SELECTION_COMPONENTS.includes(controlType as FormElementsEnum)) {
      // Range/NotBetween returns { start, end }; other numeric values are plain numbers
      if (rawValue !== null && typeof rawValue === 'object') {
        return rawValue;
      }
      return Number(rawValue);
    }
    // String and other types: use raw value as-is
    return rawValue;
  }

  private buildComparisonObject(varRef: object, value: any, logic: RuleLogicEnum, controlType: string): object {
    const isMultiSelect =
      controlType === FormElementsEnum.MultiSelect || controlType === FormElementsEnum.MultiSelectQuantity;

    switch (logic) {
      case RuleLogicEnum.Equal:
        // MultiSelect: check if value is in the selected-values array
        if (isMultiSelect) {
          return { in: [value, varRef] };
        }
        return { '==': [varRef, value] };

      case RuleLogicEnum.NotEqual:
        return { '!=': [varRef, value] };

      case RuleLogicEnum.GreaterThan:
        return { '>': [varRef, value] };

      case RuleLogicEnum.GreaterThanOrEqual:
        return { '>=': [varRef, value] };

      case RuleLogicEnum.LessThan:
        return { '<': [varRef, value] };

      case RuleLogicEnum.LessThanOrEqualTo:
        return { '<=': [varRef, value] };

      case RuleLogicEnum.Contains:
        // value is substring, varRef is the string field
        return { in: [value, varRef] };

      case RuleLogicEnum.DoesNotContain:
        return { '!': { in: [value, varRef] } };

      case RuleLogicEnum.IsEmpty:
        return { '==': [varRef, ''] };

      case RuleLogicEnum.IsNotEmpty:
        return { '!=': [varRef, ''] };

      case RuleLogicEnum.Range:
        // value = { start, end }; use 3-arg json-logic form: start <= var <= end
        return { '<=': [value.start, varRef, value.end] };

      case RuleLogicEnum.NotBetween:
        return { '!': { '<=': [value.start, varRef, value.end] } };

      default:
        return { '==': [varRef, value] };
    }
  }

  private getTranslationBase(control: ControlModel) {
    return {
      uuid: control.question_uuid,
      question_type: control.controlType,
      order: control.grid.rowStart,
      code: control.title.substring(0, 30),
      is_mandatory: true,
      logics: [],
      settings: {},
      is_deleted: control?.isDeleted || false,
      QuestionPageUuid: control.page_uuid,
    };
  }
  private translateVASControls(control: ControlModel, form: FormModel) {
    return {
      ...this.getTranslationBase(control),
      translations: [
        {
          units: '',
          name: control.label,
          ...this.getLanguage(form),
          labels: control.settings.labels.map((label: string, index: number) => ({
            order: index ? control.settings.max : control.settings.min,
            name: label,
          })),
        },
      ],
    };
  }

  private translateBooleanControls(control: ControlModel, form: FormModel) {
    return {
      ...this.getTranslationBase(control),
      translations: [
        {
          units: '',
          name: control.label,
          ...this.getLanguage(form),
          labels: control.values.map((val: { label: string }, index: number) => ({
            order: index + 1,
            name: val.label,
          })),
        },
      ],
    };
  }
  private translateNRSControls(control: ControlModel, form: FormModel) {
    const { labels, min, mid, max } = control.settings;
    // Its important that the mid should be some random number between min and max
    const orders = [min, mid, max].filter(order => order !== undefined && order !== null);
    const formattedLabels = labels.map((label: string, index: number) => ({
      order: orders[index],
      name: label,
    }));

    return {
      ...this.getTranslationBase(control),
      translations: [
        {
          units: '',
          name: control.label,
          ...this.getLanguage(form),
          labels: formattedLabels,
        },
      ],
    };
  }

  private translateVRSControls(control: ControlModel, form: FormModel) {
    return {
      ...this.getTranslationBase(control),
      translations: [
        {
          units: '',
          name: control.label,
          ...this.getLanguage(form),
          labels: control.values.map(({ label, value }) => ({ order: value, name: label })),
        },
      ],
    };
  }

  private translateTextControls(control: ControlModel, form: FormModel) {
    return {
      ...this.getTranslationBase(control),
      translations: [
        {
          units: '',
          name: control.label,
          ...this.getLanguage(form),
          labels: [],
        },
      ],
    };
  }

  private translateNumberControls(control: ControlModel, form: FormModel) {
    const { operator, additionalOperator, inputs } = control.settings;
    const settings = {
      order: 0,
      operator,
      repeats: 1,
      separator: operator,
      allowLessThanOrGreaterThan: !!additionalOperator,
      prefix_type: !additionalOperator ? 'none' : additionalOperator,
      number_integers: inputs[0].integers,
      number_decimals: inputs[0].decimals,
      values: inputs.map((input: { integers: number; decimals: number; min: number; max: number }, index: number) => ({
        order: index + 1,
        integer_places: input.integers,
        fraction: input.decimals,
        min_value: input.min,
        max_value: input.max,
      })),
    };
    return {
      ...this.getTranslationBase(control),
      translations: [
        {
          units: control.settings?.descriptor || '',
          name: control.label,
          ...this.getLanguage(form),
          labels: [],
        },
      ],
      settings,
    };
  }

  private getLanguage(form: FormModel) {
    if (form.languageId === 1) {
      // English
      return this.DEFAULT_LANGUAGE;
    } else if (form.languageId === 2) {
      // German {
      return {
        language_id: 2,
        language_code: 'de-DE',
      };
    } else {
      return this.DEFAULT_LANGUAGE;
    }
  }
}
